import type { Column, Era, Milestone, Segment, Timeline } from "@hoee/schema";
import { timeScales } from "./scale.ts";
import type { ScaleName, TimeScale } from "./scale.ts";

/** A corner of an outline: [x in column units, year]. */
export type Corner = [x: number, year: number];

/** One block: a realm in one land (or half of it) for one span of years. */
export interface Block extends Segment {
  realm: Realm;
  /** Column units: column i spans [i, i + 1]; a split lane is half of that. */
  x0: number;
  x1: number;
  /** The block's start, or where the poster draws it from when the start is unknown. */
  y0: number;
  y1: number;
  /** y0 clipped to the top of the axis. */
  cy0: number;
}

/** The rectangle where a realm's name is written. */
export interface NameBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export interface Realm {
  id: string;
  index: number;
  name: string;
  tagline: string;
  note?: string | undefined;
  landmarks: string[];
  milestones: Milestone[];
  blocks: Block[];
  /** Outline of the union of its blocks: closed loops of corners, outer loops counter-clockwise on screen. */
  loops: Corner[][];
  start: number;
  end: number;
  /** No printed start, or reaching back past the top of the axis: it fades in from above. */
  fades: boolean;
  /** Index into the ten pigments; realms that touch never share one. */
  color: number;
  /** The first and last column it ever occupies. */
  cols: [first: number, last: number];
  /**
   * The land it holds for (nearly) all its life, and which part of it: where its own story is written when it
   * is opened, always on its own paint.
   */
  home: Home;
  /** Every land it ever held. */
  lanes: number[];
  nameBox: Record<ScaleName, NameBox | null>;
}

export interface Home {
  lane: number;
  part: "full" | "left" | "right";
  /** The years it holds that part of its home. */
  from: number;
  to: number;
}

/** Years covered by a set of spans, counting overlaps once. */
function covered(spans: [number, number][]): number {
  let total = 0,
    reach = -Infinity;
  for (const [a, b] of [...spans].sort((p, q) => p[0] - q[0])) {
    const from = Math.max(a, reach);
    if (b > from) {
      total += b - from;
      reach = b;
    }
  }
  return total;
}

/**
 * The lane a realm calls home: held longest, preferring a whole land to half of one and full rule to partial
 * rule. Among equals (an empire holding many lands all its life) the one nearest the middle of its lands wins:
 * its heartland, not whichever land the chart happens to list first.
 */
export function homeOf(realm: Pick<Realm, "blocks">): Home {
  const lanes = [...new Set(realm.blocks.map((b) => Math.floor(b.x0)))].sort((a, b) => a - b);
  const middle = ((lanes[0] ?? 0) + (lanes[lanes.length - 1] ?? 0)) / 2;
  const options = lanes.map((lane) => {
    const inLane = realm.blocks.filter((b) => Math.floor(b.x0) === lane);
    const half = (side: "left" | "right", full = false) =>
      covered(
        inLane
          .filter((b) => (!b.part || b.part === side) && (!full || !b.partial))
          .map((b): [number, number] => [b.cy0, b.y1]),
      );
    const left = half("left"),
      right = half("right");
    const years = Math.max(left, right);
    // Both halves for nearly all of its life counts as the whole land.
    const part: Home["part"] = Math.min(left, right) >= 0.97 * years ? "full" : left >= right ? "left" : "right";
    const ruled = Math.max(half("left", true), half("right", true));
    const score = years * (part === "full" ? 1 : 0.9) * (0.7 + (0.3 * ruled) / Math.max(1, years));
    const held = inLane.filter((b) => !b.part || part === "full" || b.part === part);
    return { lane, part, score, from: Math.min(...held.map((b) => b.cy0)), to: Math.max(...held.map((b) => b.y1)) };
  });
  const top = Math.max(...options.map((o) => o.score));
  const best = options
    .filter((o) => o.score >= top * 0.95)
    .sort((a, b) => Math.abs(a.lane - middle) - Math.abs(b.lane - middle) || a.lane - b.lane)[0];
  return best
    ? { lane: best.lane, part: best.part, from: best.from, to: best.to }
    : { lane: 0, part: "full", from: 0, to: 0 };
}

export interface Chart {
  axis: Timeline["axis"];
  scales: Record<ScaleName, TimeScale>;
  columns: Column[];
  eras: Era[];
  realms: Realm[];
  blocks: Block[];
  realmById: ReadonlyMap<string, Realm>;
}

export const PIGMENTS = 10;

export function buildChart(timeline: Timeline): Chart {
  const { axis, columns } = timeline;
  const scales = timeScales(axis);
  const colIndex = new Map(columns.map((c, i) => [c.id, i]));
  const milestones = new Map<string, Milestone[]>();
  for (const m of timeline.milestones) milestones.set(m.entity, [...(milestones.get(m.entity) ?? []), m]);

  const blocks: Block[] = [];
  const realms: Realm[] = timeline.entities.map((e, index) => {
    const realm: Realm = {
      id: e.id,
      index,
      name: e.name,
      tagline: e.tagline,
      note: e.note,
      landmarks: e.landmarks ?? [],
      milestones: milestones.get(e.id) ?? [],
      blocks: [],
      loops: [],
      start: 0,
      end: 0,
      fades: false,
      color: 0,
      cols: [0, 0],
      home: { lane: 0, part: "full", from: 0, to: 0 },
      lanes: [],
      nameBox: { poster: null, true: null },
    };
    realm.blocks = e.segments.map((s) => {
      const x0 = (colIndex.get(s.col) ?? 0) + (s.part === "right" ? 0.5 : 0);
      const y0 = s.from ?? s.displayFrom ?? axis.start;
      const y1 = s.to ?? axis.end;
      const block: Block = { ...s, realm, x0, x1: x0 + (s.part ? 0.5 : 1), y0, y1, cy0: Math.max(y0, axis.start) };
      blocks.push(block);
      return block;
    });
    realm.fades = realm.blocks.some((b) => b.from == null || b.y0 < axis.start);
    realm.start = Math.min(...realm.blocks.map((b) => b.cy0));
    realm.end = Math.max(...realm.blocks.map((b) => b.y1));
    realm.cols = [
      Math.min(...realm.blocks.map((b) => Math.floor(b.x0))),
      Math.max(...realm.blocks.map((b) => Math.ceil(b.x1) - 1)),
    ];
    realm.loops = traceOutline(realm.blocks);
    realm.lanes = [...new Set(realm.blocks.map((b) => Math.floor(b.x0)))].sort((a, b) => a - b);
    realm.home = homeOf(realm);
    realm.nameBox = { poster: nameBox(realm.blocks, scales.poster), true: nameBox(realm.blocks, scales.true) };
    return realm;
  });
  assignColors(realms, blocks, scales.poster);
  return {
    axis,
    scales,
    columns,
    eras: timeline.eras,
    realms,
    blocks,
    realmById: new Map(realms.map((r) => [r.id, r])),
  };
}

/** Greedy colouring, biggest realms first, so that touching realms never share a pigment. */
function assignColors(realms: Realm[], blocks: Block[], scale: TimeScale): void {
  const touches = (a: Block, b: Block) => a.x0 <= b.x1 && b.x0 <= a.x1 && a.cy0 <= b.y1 && b.cy0 <= a.y1;
  const neighbours = new Map(realms.map((r) => [r, new Set<Realm>()]));
  for (let i = 0; i < blocks.length; i++) {
    for (let j = i + 1; j < blocks.length; j++) {
      const a = blocks[i] as Block,
        b = blocks[j] as Block;
      if (a.realm !== b.realm && touches(a, b)) {
        neighbours.get(a.realm)?.add(b.realm);
        neighbours.get(b.realm)?.add(a.realm);
      }
    }
  }
  const size = (r: Realm) => r.blocks.reduce((n, b) => n + (b.x1 - b.x0) * (scale.toU(b.y1) - scale.toU(b.cy0)), 0);
  const used: number[] = Array<number>(PIGMENTS).fill(0);
  const done = new Set<Realm>();
  for (const r of [...realms].sort((a, b) => size(b) - size(a))) {
    const taken = new Set([...(neighbours.get(r) ?? [])].filter((n) => done.has(n)).map((n) => n.color));
    let options = used.map((_, i) => i).filter((i) => !taken.has(i));
    if (!options.length) options = used.map((_, i) => i);
    const pick = options.reduce((best, i) => ((used[i] ?? 0) < (used[best] ?? 0) ? i : best), options[0] ?? 0);
    r.color = pick;
    used[pick] = (used[pick] ?? 0) + 1;
    done.add(r);
  }
}

/** The outline of the union of some blocks, as closed loops of corners with no corner mid-way along a side. */
export function traceOutline(blocks: readonly Block[]): Corner[][] {
  const xs = [...new Set(blocks.flatMap((b) => [b.x0, b.x1]))].sort((a, b) => a - b);
  const ys = [...new Set(blocks.flatMap((b) => [b.cy0, b.y1]))].sort((a, b) => a - b);
  const at = (list: number[], i: number) => list[i] as number;
  const cell = (i: number, j: number) => {
    if (i < 0 || j < 0 || i >= xs.length - 1 || j >= ys.length - 1) return false;
    const cx = (at(xs, i) + at(xs, i + 1)) / 2,
      cy = (at(ys, j) + at(ys, j + 1)) / 2;
    return blocks.some((b) => b.x0 < cx && cx < b.x1 && b.cy0 < cy && cy < b.y1);
  };
  const edges = new Map<string, [Corner, Corner][]>();
  const add = (a: Corner, b: Corner) => {
    const key = a.join();
    edges.set(key, [...(edges.get(key) ?? []), [a, b]]);
  };
  for (let j = 0; j < ys.length - 1; j++) {
    for (let i = 0; i < xs.length - 1; i++) {
      if (!cell(i, j)) continue;
      const [l, r, t, b] = [at(xs, i), at(xs, i + 1), at(ys, j), at(ys, j + 1)];
      if (!cell(i, j - 1)) add([l, t], [r, t]);
      if (!cell(i + 1, j)) add([r, t], [r, b]);
      if (!cell(i, j + 1)) add([r, b], [l, b]);
      if (!cell(i - 1, j)) add([l, b], [l, t]);
    }
  }
  const loops: Corner[][] = [];
  for (let first = edges.entries().next(); !first.done; first = edges.entries().next()) {
    const [startKey, list] = first.value;
    const edge = list.pop();
    if (!list.length) edges.delete(startKey);
    if (!edge) continue;
    let b = edge[1];
    const loop: Corner[] = [edge[0]];
    while (b.join() !== startKey) {
      loop.push(b);
      const nextList = edges.get(b.join());
      const next = nextList?.pop();
      if (!nextList || !next) break;
      if (!nextList.length) edges.delete(b.join());
      b = next[1];
    }
    const n = loop.length;
    loops.push(
      loop.filter((p, k) => {
        const prev = loop[(k - 1 + n) % n] as Corner,
          next = loop[(k + 1) % n] as Corner;
        return !((prev[0] === p[0] && p[0] === next[0]) || (prev[1] === p[1] && p[1] === next[1]));
      }),
    );
  }
  return loops;
}

/** The biggest rectangle a realm covers in a given scale, spanning neighbouring lands where it can. */
export function nameBox(blocks: readonly Block[], scale: TimeScale): NameBox | null {
  const fullByCol = new Map<number, [number, number][]>();
  for (const b of blocks) {
    const ci = Math.floor(b.x0);
    const list = fullByCol.get(ci) ?? [];
    if (!b.part) list.push([b.cy0, b.y1]);
    fullByCol.set(ci, list);
  }
  for (const [ci, list] of fullByCol) {
    const lefts = blocks.filter((b) => b.part === "left" && Math.floor(b.x0) === ci);
    const rights = blocks.filter((b) => b.part === "right" && Math.floor(b.x0) === ci);
    for (const l of lefts)
      for (const r of rights) {
        const lo = Math.max(l.cy0, r.cy0),
          hi = Math.min(l.y1, r.y1);
        if (hi > lo) list.push([lo, hi]);
      }
  }
  let best: (NameBox & { area: number }) | null = null;
  const consider = (x0: number, x1: number, y0: number, y1: number) => {
    const area = (x1 - x0) * (scale.toU(y1) - scale.toU(y0));
    if (area > 0 && (!best || area > best.area)) best = { x0, x1, y0, y1, area };
  };
  for (const b of blocks) consider(b.x0, b.x1, b.cy0, b.y1);
  for (const [ci, list] of fullByCol) {
    for (const [a, b] of list) {
      // The whole land, where its two halves are held at once.
      consider(ci, ci + 1, a, b);
      let lo = a,
        hi = b,
        c = ci;
      while (fullByCol.has(c + 1)) {
        const next = (fullByCol.get(c + 1) ?? [])
          .map(([p, q]): [number, number] => [Math.max(lo, p), Math.min(hi, q)])
          .filter(([p, q]) => q > p)
          .sort((m, n) => n[1] - n[0] - (m[1] - m[0]))[0];
        if (!next) break;
        [lo, hi] = next;
        c++;
        consider(ci, c + 1, lo, hi);
      }
    }
  }
  if (!best) return null;
  const { x0, x1, y0, y1 } = best;
  return { x0, x1, y0, y1 };
}

/**
 * Who holds a realm's home in a given year: the realm whose block covers that lane (and the same half of it)
 * then. Scrolling along an open realm's land hands it over to this one when you pass its years.
 */
export function holderOf(chart: Chart, home: Pick<Home, "lane" | "part">, year: number): Realm | null {
  const inLane = chart.blocks.filter((b) => Math.floor(b.x0) === home.lane && b.cy0 <= year && year < b.y1);
  const same = inLane.filter((b) => !b.part || home.part === "full" || b.part === home.part);
  // A whole-land holder first; else, for a whole-land home, the larger half's holder, which is the left by
  // the chart's convention.
  const pick =
    same.find((b) => !b.part) ?? same.find((b) => b.part === (home.part === "right" ? "right" : "left")) ?? same[0];
  return pick?.realm ?? null;
}

/** The realms that hold a realm's home just before and just after it: where scrolling along its land leads. */
export function neighboursOf(chart: Chart, realm: Realm): Realm[] {
  const out = new Set<Realm>();
  for (const year of [realm.home.from - 0.5, realm.home.to + 0.5]) {
    const r = holderOf(chart, realm.home, year);
    if (r && r !== realm) out.add(r);
  }
  return [...out];
}
